import { type UseQueryResult, keepPreviousData, useQuery } from "@tanstack/react-query";
import type { Item } from "src/types/item";
import type { Pos } from "src/types/pos";
import { Query } from "src/types/query";
import type { User } from "src/types/user";
import axios from "src/utils/axios";

export function getProductListQueryArgument(
    posList: Pos[],
    selectedSource: string,
    user: User
): {
    queryArg: string[];
    possString: string;
} {
    let possString = posList
        .map((e) => e.id)
        .filter((posId) => posId > 0)
        .sort()
        .map((posId) => `&pos=${posId}`)
        .join("");
    if (possString === "") {
        possString = user.poss.map((pos) => `&pos=${pos.id}`).join("");
    }
    return {
        queryArg: [Query.ProductList, possString, selectedSource],
        possString,
    };
}

export function useQueryProductList(
    user: User,
    {
        selectedSource,
        selectedPosList,
        isEnabled,
    }: {
        selectedSource: string;
        isEnabled?: boolean;
        selectedPosList: Pos[];
    }
): UseQueryResult<Item[], unknown> {
    const currentArg = getProductListQueryArgument(selectedPosList, selectedSource, user);

    return useQuery({
        queryKey: currentArg.queryArg,

        queryFn: async () => {
            const { data } = await axios.get<{ items: Item[] }>(
                `/organization/${user.organization.id}/remotechannelitem?source=${selectedSource}&pos=${currentArg.possString}`
            );
            const products = data.items;
            const newItemsWithPosIds = Object.values(
                products
                    .filter((item) => item.isActive && item.remotePrice > 0)
                    .reduce((a, item) => {
                        const currentName = `${item.name}-${item.remoteCategory?.name}`;
                        // Pour l'instant le choix est fait de grouper les items de 2 catégories ayant le même nom
                        if (a[currentName] && !a[currentName].posIds.includes(item.posId)) {
                            a[currentName].posIds.push(item.posId);
                        } else if (!a[currentName]) {
                            a[currentName] = {
                                ...item,
                                id: `${item.name}-${item.remoteCategory?.name}`,
                            };
                            a[currentName].posIds = [item.posId];
                        }
                        return a;
                    }, {})
            ).flat() as Item[];
            return newItemsWithPosIds ?? [];
        },

        initialData: [],
        placeholderData: keepPreviousData,
        enabled: isEnabled === false ? isEnabled : Boolean(selectedSource && currentArg.possString),
    });
}
